import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchCompany, updateCompany } from "../api/company";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  company: null,
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const updateCompanyAsync = createAsyncThunk(
  "company/updateCompanyAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateCompany(payload);
      const body = response?.data ?? response;
      const status = response?.status ?? body?.status;
      if (
        isHttpFailureStatus(status) ||
        body?.error ||
        hasMeaningfulErrors(body?.errors)
      ) {
        return rejectNormalized(rejectWithValue, body, "update_company");
      }
      return body;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_company");
    }
  }
);

export const getCompanyAsync = createAsyncThunk(
  "company/getCompanyAsync",
  async (_arg, { rejectWithValue }) => {
    try {
      const response = await fetchCompany();
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_company");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_company");
    }
  }
);

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getCompanyAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.company = action.payload;
      })
      .addCase(getCompanyAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente bedriften.");
      })
      .addCase(updateCompanyAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateCompanyAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.company = action.payload;
      })
      .addCase(updateCompanyAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere bedriften.");
      });
  },
});

export const { reducer } = companySlice;

export default companySlice;
